import Header from "../components/shared/Header";
import Footer from "../components/shared/Footer";
import SEOHead from "../components/shared/SEOHead";
import Breadcrumbs from "../components/shared/Breadcrumbs";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";
import { createFAQSchema } from "../seo/schemas";

const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      category: "Booking & Quotes",
      questions: [
        { q: "How far in advance should I book?", a: "We recommend booking 2-4 weeks ahead, especially for weekends and end-of-month dates which are our busiest times. However, we can often accommodate last-minute moves - just give us a call." },
        { q: "Is the online quote accurate?", a: "Our instant quote provides an accurate estimate for straightforward moves. For larger properties or those with specialist items, we recommend a free survey for the most accurate price." },
        { q: "What deposit is required?", a: "We require a 25% deposit to secure your booking. The remaining balance is due on the day of the move." },
        { q: "Can I change my move date?", a: "Yes, you can change your date free of charge with at least 7 days' notice. Changes with less notice may incur a small fee depending on availability." }
      ]
    },
    {
      category: "The Move",
      questions: [
        { q: "Do you provide packing materials?", a: "Yes, we offer a complete packing service and can supply all necessary materials including boxes, bubble wrap, and tape. These can be included in your quote or purchased separately." },
        { q: "How long will my move take?", a: "This depends on the size of your home and distance. A typical 2-bedroom flat takes 4-6 hours for a local move. We'll give you an estimated timeframe with your quote." },
        { q: "Do I need to be present during the move?", a: "We prefer you to be present at both properties, but we can make arrangements if this isn't possible. You'll need to designate someone to oversee the move on your behalf." },
        { q: "What happens if something gets damaged?", a: "All moves are fully insured up to £50,000 as standard. In the unlikely event of damage, we'll work with you to resolve it quickly and fairly." }
      ]
    },
    {
      category: "Special Requirements",
      questions: [
        { q: "Can you move a piano?", a: "Yes, we have trained piano movers and specialist equipment. Piano moves require a free survey to provide an accurate quote and ensure we bring the right team." },
        { q: "Do you offer storage?", a: "Yes, we have secure, climate-controlled storage facilities available from just £50 per week. This can be combined with your move or arranged separately." },
        { q: "Can you handle antiques and fragile items?", a: "Absolutely. Our specialist team has experience moving valuable and delicate items. We use custom packaging and extra care protocols for these moves." },
        { q: "Do you do office moves?", a: "Yes, we provide full commercial removal services including weekend moves to minimize business disruption. All office moves include a free survey and project management." }
      ]
    },
    {
      category: "Payment & Cancellation",
      questions: [
        { q: "What payment methods do you accept?", a: "We accept card payments (via Stripe), PayPal, and bank transfer. Payment can be made online or in person on the day of your move." },
        { q: "What is your cancellation policy?", a: "Cancellations with more than 7 days' notice receive a full refund. Cancellations with less notice may forfeit the deposit, depending on circumstances." },
        { q: "Are there any hidden fees?", a: "No. The price we quote is the price you pay, provided the details you've given us are accurate. Any additional requirements discovered on the day will be discussed with you first." }
      ]
    }
  ];

  const toggleFAQ = (categoryIndex, questionIndex) => {
    const key = `${categoryIndex}-${questionIndex}`;
    setOpenIndex(openIndex === key ? null : key);
  };

  const allFaqs = faqs.flatMap(cat => cat.questions);
  const faqSchema = createFAQSchema(allFaqs);

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Removals FAQ | Common Moving Questions | Hitech Transit"
        description="Answers to common questions about removals in Cardiff. Pricing, booking, packing, insurance, cancellations and more. Everything you need to know before your move."
        canonical="/faq"
        jsonLd={faqSchema}
      />
      <Header />
      
      <main className="pt-20">
        <Breadcrumbs items={[{ label: "FAQs", href: "/faq" }]} />
        {/* Hero */}
        <section className="bg-[#0F172A] py-20">
          <div className="container-max px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF5A1F] mb-4 block">
                Help & Support
              </span>
              <h1 className="text-4xl md:text-5xl font-['Outfit'] font-bold text-white tracking-tight mb-6">
                Frequently Asked Questions
              </h1>
              <p className="text-slate-300 text-lg">
                Find answers to common questions about our removal services, booking process, 
                and what to expect on moving day.
              </p>
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="section-padding">
          <div className="container-max">
            <div className="max-w-3xl mx-auto space-y-12">
              {faqs.map((category) => (
                <div key={category.category}>
                  <h2 className="text-xl font-['Outfit'] font-bold text-[#0F172A] mb-6">
                    {category.category}
                  </h2>
                  <div className="space-y-4">
                    {category.questions.map((faq) => {
                      const catIdx = faqs.indexOf(category);
                      const qIdx = category.questions.indexOf(faq);
                      const isOpen = openIndex === `${catIdx}-${qIdx}`;
                      return (
                        <div key={faq.q} className="border border-slate-200">
                          <button
                            onClick={() => toggleFAQ(catIdx, qIdx)}
                            className="w-full flex items-center justify-between p-6 text-left hover:bg-slate-50 transition-colors"
                            data-testid={`faq-${catIdx}-${qIdx}`}
                          >
                            <span className="font-semibold text-[#0F172A] pr-4">{faq.q}</span>
                            {isOpen ? (
                              <ChevronUp className="w-5 h-5 text-[#FF5A1F] flex-shrink-0" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
                            )}
                          </button>
                          {isOpen && (
                            <div className="px-6 pb-6">
                              <p className="text-slate-600 leading-relaxed">{faq.a}</p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Still Have Questions */}
        <section className="section-padding bg-[#F8F9FA]">
          <div className="container-max text-center">
            <h2 className="text-3xl font-['Outfit'] font-bold text-[#0F172A] mb-6">
              Still Have Questions?
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto mb-8">
              Our friendly team is here to help. Get in touch and we'll get back to you as soon as possible.
            </p>
            <Link to="/contact" className="btn-primary inline-flex items-center gap-2" data-testid="faq-contact-btn">
              Contact Us
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default FAQPage;
